import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Contexte d'univers actif côté client. Permet de distinguer :
 *   - le joueur est "dans un univers" (gameplay actif)
 *   - le joueur est "à la maison" (peut accéder au lobby pour changer
 *     d'univers ou en rejoindre un nouveau)
 *
 * Tracke aussi la **dernière planète visitée** + son contexte cosmique
 * (galaxie / système) pour que la navigation puisse offrir un raccourci
 * "Système Solaire" qui amène l'utilisateur au contexte de sa planète
 * courante. Cf wiki §32 (sitemap) et le bouton sidebar correspondant.
 *
 * Persisté en localStorage pour survivre aux reload. Indépendant du
 * JWT (l'auth reste active). Reset lors d'un logout via `clear()`.
 */
const STORAGE_KEY = 'theho.session.activePlayerUniverseId';
const STORAGE_UNIVERSE_ID = 'theho.session.activeUniverseId';
const STORAGE_LAST_PLANET = 'theho.session.lastPlanetContext';

interface LastPlanetContext {
  planetId: number;
  galaxyNumber: number;
  solarSystemNumber: number;
  /** Nom de la planète (pour le fil d'Ariane : « Planète X » au lieu de « #id »). */
  planetName?: string;
}

function readStoredId(): number | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function readStoredUniverseId(): number | null {
  const raw = localStorage.getItem(STORAGE_UNIVERSE_ID);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function readLastPlanet(): LastPlanetContext | null {
  const raw = localStorage.getItem(STORAGE_LAST_PLANET);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<LastPlanetContext>;
    if (typeof parsed.planetId !== 'number' || parsed.planetId <= 0) return null;
    if (typeof parsed.galaxyNumber !== 'number' || parsed.galaxyNumber <= 0) return null;
    if (typeof parsed.solarSystemNumber !== 'number' || parsed.solarSystemNumber <= 0) return null;
    return parsed as LastPlanetContext;
  } catch { return null; }
}

/** Parse une chaine de coordonnees `[galaxy:system:planet]` (cf
 * UniverseGenerationHelpers.FormatCoordinates). Retourne null si le format
 * est invalide -- le caller decidera de ne pas tracker le contexte. */
export function parseCoordinates(coords: string | null | undefined): { galaxy: number; system: number; planet: number } | null {
  if (!coords) return null;
  const m = /^\[(\d+):(\d+):(\d+)\]$/.exec(coords.trim());
  if (!m) return null;
  return { galaxy: Number(m[1]), system: Number(m[2]), planet: Number(m[3]) };
}

export const useSessionStore = defineStore('session', () => {
  const activePlayerUniverseId = ref<number | null>(readStoredId());
  const activeUniverseId = ref<number | null>(readStoredUniverseId());
  const lastPlanet = ref<LastPlanetContext | null>(readLastPlanet());

  const inUniverse = computed(() => activePlayerUniverseId.value !== null);
  const hasLastPlanetContext = computed(() => lastPlanet.value !== null);

  /** Marque le PlayerUniverse actif. Le second parametre (universeId) est
   * optionnel pour compat avec les anciens callers ; quand fourni, il
   * permet a UniverseMapView de construire son URL sans aller-retour DB. */
  function setActive(playerUniverseId: number, universeId?: number): void {
    activePlayerUniverseId.value = playerUniverseId;
    localStorage.setItem(STORAGE_KEY, String(playerUniverseId));
    if (typeof universeId === 'number' && universeId > 0) {
      activeUniverseId.value = universeId;
      localStorage.setItem(STORAGE_UNIVERSE_ID, String(universeId));
    }
  }

  /** Memorise la planete courante + son systeme/galaxie pour le
   * raccourci "Systeme Solaire" de la sidebar. Appele par PlanetHomeView
   * au chargement de la dashboard. */
  function setLastPlanetContext(
    planetId: number, coordinates: string | null | undefined, planetName?: string | null): void {
    const parsed = parseCoordinates(coordinates);
    if (!parsed) return; // coords mal formees -> on ne touche pas le contexte
    const ctx: LastPlanetContext = {
      planetId,
      galaxyNumber: parsed.galaxy,
      solarSystemNumber: parsed.system,
      planetName: planetName || undefined,
    };
    lastPlanet.value = ctx;
    localStorage.setItem(STORAGE_LAST_PLANET, JSON.stringify(ctx));
  }

  function clear(): void {
    activePlayerUniverseId.value = null;
    activeUniverseId.value = null;
    lastPlanet.value = null;
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_UNIVERSE_ID);
    localStorage.removeItem(STORAGE_LAST_PLANET);
  }

  return {
    activePlayerUniverseId,
    activeUniverseId,
    inUniverse,
    lastPlanet,
    hasLastPlanetContext,
    setActive,
    setLastPlanetContext,
    clear,
  };
});
